import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter, take, timeout } from 'rxjs/operators';
import { StripeFacade } from '@app/store/stripe';
import { LoggerService } from '@app/core/infrastructure';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@app/popups/confirmation-dialog/confirmation-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionGuard implements CanActivate {
  private readonly logger = inject(LoggerService).scope('SubscriptionGuard');

  constructor(
    private stripeFacade: StripeFacade,
    private router: Router,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Trigger subscription load
    this.stripeFacade.loadUserSubscription();

    // Wait for subscription loading to complete before checking
    return this.stripeFacade.userSubscriptionLoading$.pipe(
      // Wait until loading is false (data has been loaded)
      filter(loading => !loading),
      take(1),
      // Add timeout to prevent infinite waiting
      timeout(5000),
      // Once loading is complete, switch to subscription data
      switchMap(() => this.stripeFacade.userSubscription$),
      take(1),
      map(subscription => {
        this.logger.debug('Subscription check:', {
          hasSubscription: subscription?.has_subscription,
          isActive: subscription?.is_active,
          isTrial: subscription?.is_trial,
          canUseService: subscription?.can_use_service
        });

        // Allow access if user has active subscription
        const hasActiveSubscription = subscription?.has_subscription && subscription?.is_active;
        
        // Allow trial users to navigate only if they can still use the service
        // When trial expires (can_use_service=false), redirect to pricing
        const isActiveTrial = subscription?.is_trial && subscription?.can_use_service;
        
        if (hasActiveSubscription || isActiveTrial) {
          return true;
        }

        // If user is already on /plans or /billing, show a friendly popup instead of silent redirect
        const currentUrl = this.router.url;
        if (currentUrl.startsWith('/plans') || currentUrl.startsWith('/billing')) {
          this.showSubscriptionRequiredDialog();
          return false;
        }

        // Otherwise, redirect to plans page
        this.logger.debug('Redirecting to plans - no active subscription or trial');
        return this.router.createUrlTree(['/plans'], {
          queryParams: { returnUrl: state.url }
        });
      }),
      catchError(error => {
        this.logger.error('Subscription check failed:', error);
        // On error, redirect to plans page
        return of(this.router.createUrlTree(['/plans']));
      })
    );
  }

  private showSubscriptionRequiredDialog(): void {
    // Prevent stacking multiple dialogs on rapid clicks
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('subscriptionRequired.title'),
        message: this.translate.instant('subscriptionRequired.message'),
        iconName: 'lock',
        confirmButtonText: this.translate.instant('subscriptionRequired.okGotIt'),
        hideCancelButton: true,
      } as ConfirmationDialogData,
      width: '420px',
    });
  }
}
